import moment from 'moment';
import { cloneDeep } from 'lodash';

import { truncateTextWithEllipsis } from '@utilities';

import { surveyDatePromptIds } from '@api/surveys/utilities/constants';

const PILL_DATE_FORMAT = 'MM/DD/YYYY';

/**
 * @typedef {Object} PromptAnswerValue value returned from Insights prompt searching
 * @property {String} attributeId eg. "sector"
 * @property {String} id eg. "sector:isc_apparel"
 * @property {Boolean} is_fixed
 * @property {String} name
 * @property {String} prompt_desc
 */

/**
 * @typedef {Object} PromptAnswer
 * @property {String} [field]
 * @property {String} fieldType
 * @property {Number|String} [id]
 * @property {Number} promptId
 * @property {Array<PromptAnswerValue|String>} values
 */

const getAnswerValueKey = (value) => {
  if (value && typeof value === 'object') {
    if (value.id !== undefined && value.id !== null) {
      return `id:${value.id}`;
    }
    if (value.attributeId || value.field) {
      return `attr:${value.attributeId || value.field}:${value.name || ''}`;
    }
    return JSON.stringify(value);
  }
  return `${typeof value}:${String(value)}`;
};

const dedupeAnswerValues = (values = []) => {
  const seen = new Set();
  const result = [];
  (values || []).forEach((value) => {
    if (value === null || typeof value !== 'object') {
      result.push(cloneDeep(value));
      return;
    }

    const key = getAnswerValueKey(value);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(cloneDeep(value));
  });
  return result;
};

export const normalizePromptAnswer = (answer = {}) => {
  const cloned = cloneDeep(answer);
  cloned.values = dedupeAnswerValues(cloned.values);
  if (cloned._delete && cloned.values.length > 0) {
    delete cloned._delete;
  }
  return cloned;
};

export const normalizePromptAnswers = (answers = []) =>
  (answers || []).map((answer) => normalizePromptAnswer(answer));

/**
 * transformPromptSelections
 * @param {Object[]|String[]|Object} options.answers
 * @param {Object} options.prompt
 * @return {PromptAnswer[]}
 */
export const transformPromptSelections = ({ answers, prompt }) => {
  if (!answers || answers.length === 0) return [];
  const isDateRange = surveyDatePromptIds.includes(prompt.insightsKey);

  if (prompt.promptKey === 'comparison_period') {
    if (answers && answers?.comparison_period) {
      return normalizePromptAnswers([
        {
          fieldType: 'COMPARISON_PERIOD',
          promptId: prompt?.id,
          values: answers.comparison_period,
        },
      ]);
    }

    return normalizePromptAnswers([
      {
        fieldType: 'COMPARISON_PERIOD',
        promptId: prompt?.id,
        values: answers,
      },
    ]);
  }

  if (isDateRange) {
    // for point forward date ranges
    if (answers.length === 1 && typeof answers[0] === 'string') {
      return normalizePromptAnswers([
        {
          field: prompt.attributes[0]?.field,
          fieldType: 'DATE',
          operator: 'GREATER_THAN_OR_EQUAL',
          promptId: prompt.id,
          values: answers,
        },
      ]);
    }

    if (answers.date_range) {
      return normalizePromptAnswers([
        {
          field: prompt.attributes[0]?.field,
          fieldType: 'DATE',
          operator: 'IN_RANGE',
          promptId: prompt.id,
          values: answers.date_range,
        },
      ]);
    }

    return normalizePromptAnswers([
      {
        field: prompt.attributes[0]?.field,
        fieldType: 'DATE',
        operator: 'IN_RANGE',
        promptId: prompt.id,
        values: answers,
      },
    ]);
  }

  const isCustomFilterPrompt =
    prompt.insightsKey === 'purchase_shift' ||
    prompt.insightsKey === 'purchase_volume' ||
    prompt.insightsKey === 'unique_brands';

  if (isCustomFilterPrompt) {
    return normalizePromptAnswers(answers);
  }

  const defaultField = prompt.attributes?.[0]?.field;
  const promptAnswers = [];
  answers.forEach((answer) => {
    const field =
      answer.field ||
      prompt.attributes?.find(({ id }) => answer.attributeId === id)?.field ||
      defaultField;
    const existingAnswer = promptAnswers.find(
      ({ field: existingField }) => existingField === field
    );
    if (existingAnswer) {
      existingAnswer.operator = 'IN_LIST';
      existingAnswer.values.push(answer);
    } else {
      promptAnswers.push({
        field,
        fieldType: 'TEXT',
        operator: 'EQUALS',
        promptId: prompt.id,
        values: [answer],
      });
    }
  });

  return normalizePromptAnswers(promptAnswers);
};

/**
 * gets value for pill based on current prompt and answers
 * @param {Object} options
 * @param {Number} [options.firstAnswerMaxLength]
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {String}
 */
export const getPromptPillValue = ({ firstAnswerMaxLength = 0, prompt, promptAnswers = [] }) => {
  if (prompt.promptKey === 'comparison_period') {
    if (promptAnswers?.length === 0) return prompt?.emptyValue;
    const selectedIds = promptAnswers
      .filter(({ promptId }) => promptId === prompt.id)
      .flatMap(({ values = [] }) => values);

    if (selectedIds.length === 0) return prompt?.emptyValue;

    return prompt?.dateOptions?.find((dateOption) =>
      dateOption.options.some(({ value }) => selectedIds.includes(value[0]))
    )?.timeFrame;
  }

  if (prompt?.insightsKey === 'purchase_volume' || prompt?.insightsKey === 'purchase_shift') {
    const attribute = prompt.attributes?.find(
      (attr) => attr.id === promptAnswers?.[0]?.attributeId
    );
    const name = attribute?.name;
    return name || prompt?.emptyValue;
  }
  const answers = promptAnswers
    .filter(({ _delete, promptId }) => !_delete && promptId === prompt.id)
    .map(({ values }) => values)
    .flat(1);

  if (answers.length === 0) return prompt?.emptyValue;

  const isDateRange = surveyDatePromptIds.includes(prompt.insightsKey);

  if (isDateRange) {
    // for point forward date ranges
    if (answers.length === 1 && typeof answers[0] === 'string') {
      return `${moment(answers[0]).format(PILL_DATE_FORMAT)} or later`;
    }

    if (answers.length === 2 && typeof answers[0] === 'string') {
      return [answers[0], answers[1]]
        .map((value) => moment(value))
        .sort((a, b) => a.diff(b)) // values not guaranteed to be in order, sort is necessary
        .map((date) => date.format(PILL_DATE_FORMAT))
        .join(' to ');
    }

    return answers[0]?.date_range
      ?.map((value) => moment(value).format(PILL_DATE_FORMAT))
      .join(' to ');
  }

  const firstAnswerAttribute = prompt?.attributes?.find(
    (attribute) => attribute?.field === answers[0].attributeId
  );

  const firstAnswerName = firstAnswerMaxLength
    ? truncateTextWithEllipsis({
        length: answers.length > 1 ? firstAnswerMaxLength : firstAnswerMaxLength + 10,
        text: answers[0].name,
      })
    : answers[0].name;

  const firstAnswer =
    firstAnswerAttribute?.name && prompt?.isPrefixIncluded && prompt?.insightsKey !== 'new_item'
      ? `${firstAnswerAttribute?.name}: ${firstAnswerName}`
      : firstAnswerName;

  if (answers.length > 1) {
    return `${firstAnswer} ${prompt.isSortEnabled ? 'then' : 'or'} ${answers.length - 1} more`;
  }
  return firstAnswer;
};

/**
 * gets tooltip content for pill based on current prompt and answers
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {String}
 */
export const getPromptPillTooltip = ({ prompt, promptAnswers = [] }) => {
  const answers = promptAnswers
    .filter(({ _delete, promptId }) => !_delete && promptId === prompt.id)
    .map(({ values }) => values)
    .flat(1);

  const isDateRange = surveyDatePromptIds.includes(prompt?.insightsKey);
  const isPurchaseVolume = prompt?.insightsKey === 'purchase_volume';
  const isPurchaseShift = prompt?.insightsKey === 'purchase_shift';

  if (answers.length <= 1 || isDateRange || isPurchaseVolume || isPurchaseShift) return null;

  return answers.map((value, index) => {
    return (
      <div key={`selection-${index}`}>
        <small>
          {getPromptPillValue({
            prompt,
            promptAnswers: [{ promptId: prompt.id, values: [value] }],
          })}
        </small>
      </div>
    );
  });
};
